import React, { useState } from "react";
import { Home, FileText, Star, ArrowLeft, Trophy } from "lucide-react";
import BlogPage from "./components/Blog";
import RecommendationsPage from "./components/Recommendations";
import AccomplishmentsPage from "./components/Accomplishments";
import { Card, CardContent } from "./components/ui/card";

const TestContentApp = () => {
  const [currentPage, setCurrentPage] = useState("home");

  const renderPage = () => {
    switch (currentPage) {
      case "blog":
        return <BlogPage />;
      case "recommendations":
        return <RecommendationsPage />;
      case "accomplishments":
        return (
          <AccomplishmentsPage
            showBackToHome={true}
            onBack={() => setCurrentPage("home")}
          />
        );
      default:
        return <HomePage setCurrentPage={setCurrentPage} />;
    }
  };

  const showNavigation = currentPage !== "home";

  return (
    <div className="min-h-screen">
      {/* Navigation for sub-pages */}
      {showNavigation && (
        <nav className="bg-white/90 backdrop-blur-sm shadow-sm border-b sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <button
              onClick={() => setCurrentPage("home")}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Content Hub
            </button>
            <div className="flex gap-4">
              <button
                onClick={() => setCurrentPage("blog")}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300 ${
                  currentPage === "blog"
                    ? "bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700"
                    : "text-gray-600 hover:text-blue-600"
                }`}
              >
                Blog
              </button>
              <button
                onClick={() => setCurrentPage("recommendations")}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300 ${
                  currentPage === "recommendations"
                    ? "bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700"
                    : "text-gray-600 hover:text-blue-600"
                }`}
              >
                Recommendations
              </button>
              <button
                onClick={() => setCurrentPage("accomplishments")}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300 ${
                  currentPage === "accomplishments"
                    ? "bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700"
                    : "text-gray-600 hover:text-blue-600"
                }`}
              >
                Accomplishments
              </button>
            </div>
          </div>
        </nav>
      )}
      
      {renderPage()}
    </div>
  );
};

const HomePage = ({ setCurrentPage }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="py-20 px-4 text-center">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-6">
            Content Hub
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 font-light mb-4">
            Testing ground for AI-managed content
          </p>
          <p className="text-lg text-gray-600 italic">
            Explore blog posts and recommendations before going live
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 pb-20 space-y-12">
        {/* Content Sections */}
        <section className="grid md:grid-cols-2 gap-6">
          {/* Blog Section */}
          <Card className="overflow-hidden border-none shadow-lg bg-white/80 backdrop-blur-sm hover:-translate-y-1 transition-transform duration-300">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-lg bg-gradient-to-b from-blue-50 to-blue-100">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <h2 className="text-2xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                  Thoughts & Writing
                </h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-6">
                Blog posts about AI safety, infrastructure, transportation, and building for the future. 
                Technical insights made accessible.
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Sample Posts:</span>
                  <span className="bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 px-2 py-1 rounded-full text-xs">
                    1 post
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 px-2 py-1 rounded-full text-xs">
                    Personal
                  </span>
                  <span className="bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 px-2 py-1 rounded-full text-xs">
                    Meta
                  </span>
                </div>
              </div>
              <button
                onClick={() => setCurrentPage("blog")}
                className="w-full bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 py-3 rounded-lg hover:scale-105 transition-transform duration-300 font-medium"
              >
                Explore Blog →
              </button>
            </CardContent>
          </Card>

          {/* Recommendations Section */}
          <Card className="overflow-hidden border-none shadow-lg bg-white/80 backdrop-blur-sm hover:-translate-y-1 transition-transform duration-300">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-lg bg-gradient-to-b from-purple-50 to-purple-100">
                  <Star className="w-6 h-6 text-purple-600" />
                </div>
                <h2 className="text-2xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                  Recommendations
                </h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-6">
                Curated books, tools, and resources that I find valuable and worth sharing. 
                Real recommendations from actual experience.
              </p>
              <div className="space-y-3 mb-6">
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-600">2</div>
                    <div className="text-gray-600">Books</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-blue-600">2</div>
                    <div className="text-gray-600">Tools</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-purple-600">1</div>
                    <div className="text-gray-600">Resources</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 px-2 py-1 rounded-full text-xs">
                    AI Safety
                  </span>
                  <span className="bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 px-2 py-1 rounded-full text-xs">
                    Infrastructure
                  </span>
                  <span className="bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 px-2 py-1 rounded-full text-xs">
                    Urban Planning
                  </span>
                </div>
              </div>
              <button
                onClick={() => setCurrentPage("recommendations")}
                className="w-full bg-gradient-to-r from-purple-50 to-purple-100 text-purple-700 py-3 rounded-lg hover:scale-105 transition-transform duration-300 font-medium"
              >
                Browse Recommendations →
              </button>
            </CardContent>
          </Card>

          {/* Accomplishments Section */}
          <Card className="overflow-hidden border-none shadow-lg bg-white/80 backdrop-blur-sm hover:-translate-y-1 transition-transform duration-300 md:col-span-2">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-lg bg-gradient-to-b from-yellow-50 to-yellow-100">
                  <Trophy className="w-6 h-6 text-yellow-700" />
                </div>
                <h2 className="text-2xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                  Tech Accomplishments
                </h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-6">
                A simple page for hackathons and GitHub projects — designed to
                be easy to maintain from <code className="bg-gray-100 px-2 py-1 rounded">/src/data/content.js</code>.
              </p>
              <button
                onClick={() => setCurrentPage("accomplishments")}
                className="w-full bg-gradient-to-r from-yellow-50 to-yellow-100 text-yellow-800 py-3 rounded-lg hover:scale-105 transition-transform duration-300 font-medium"
              >
                View Accomplishments →
              </button>
            </CardContent>
          </Card>
        </section>

        {/* Info Section */}
        <section className="transform hover:-translate-y-1 transition-transform duration-300">
          <Card className="border-none shadow-lg bg-white/80 backdrop-blur-sm">
            <CardContent className="p-8">
              <h2 className="text-2xl font-semibold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                AI Content Management System
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-medium mb-3 text-gray-800">How It Works</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• Content stored in <code className="bg-gray-100 px-2 py-1 rounded">/src/data/content.js</code></li>
                    <li>• AI assistants follow templates in <code className="bg-gray-100 px-2 py-1 rounded">.cursorrules</code></li>
                    <li>• Changes committed to GitHub automatically</li>
                    <li>• AWS Amplify deploys updates in ~2-3 minutes</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-3 text-gray-800">Try It Out</h3>
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4">
                    <p className="text-gray-700 text-sm mb-2">
                      <strong>Example command:</strong>
                    </p>
                    <code className="text-xs text-blue-700 bg-blue-50 px-2 py-1 rounded block">
                      "Claude, add a blog post about debugging OpenTofu state issues"
                    </code>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Back to Main Site */}
        <section className="text-center">
          <a
            href="/"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:scale-105 transition-transform duration-300 font-medium"
          >
            <Home className="w-4 h-4" />
            Back to Main Site
          </a>
        </section>
      </main>

      <footer className="text-center py-8 text-gray-600">
        <p>&copy; 2024 Mark Ballew - Content Testing Environment</p>
      </footer>
    </div>
  );
};

export default TestContentApp; 
